/**
 * store 狀態 → 各純展示元件 props 的轉接器。
 *
 * 全部是純函式（輸入 project + 視圖狀態，輸出 props 用的資料），沒有 React、
 * 沒有 zustand ——這樣整合測試可以直接對它們斷言，不必先掛一棵元件樹。
 */

import {
  rawNumber,
  type Assignment,
  type BaselineNumber,
  type CustomFieldDefinition,
  type LevelingResult,
  type Project,
  type Resource,
  type Task,
  type TaskId,
} from '@bigint-project/core';

import { toGanttTask } from '../components/gantt/adapt';
import type { GanttTask } from '../components/gantt/types';
import { DEFAULT_COLUMNS as GRID_DEFAULT_COLUMNS } from '../components/grid/columns';
import type { ColumnDef as GridColumnDef } from '../components/grid/types';
import { getVisibleTasks } from '../components/grid/visibility';
import type {
  LevelingComparisonRow,
  ResourceGraphSeries,
  ResourceSheetRow,
  UsageGroupRow,
} from '../components/resource/types';
import type {
  CostTableRow,
  EacComparisonEntry,
  EvmCurvePoint,
  EvmDiagnosticView,
  EvmMetricsView,
} from '../components/cost/types';
import type { ColumnDef as StoreColumnDef } from '../store/types';
import {
  progressAsOf,
  selectBaselineTaskSnapshots,
  selectCostResult,
  selectDailyByAssignment,
  selectEacComparison,
  selectEvmResult,
  selectExpectedProgressByTask,
  selectOverallocatedDaysByResource,
  selectOverallocatedResourceIds,
  selectResourceNamesByTask,
} from './derivations';

// ---------------------------------------------------------------------------
// 欄位定義：store 的 ColumnDef 與 grid 的 ColumnDef 不同形狀
// ---------------------------------------------------------------------------

/**
 * store 的 `ColumnDef`（`{id, field, label, width, visible}`，供 `.bproj` 持久化）
 * 與 grid 的 `ColumnDef`（`{id, field, title, kind, width, editable}`，供渲染）
 * 是兩個不同的型別。`kind`/`editable` 只有 grid 那份有，因此以 id 對回
 * `GRID_DEFAULT_COLUMNS` 補齊；對不到的欄位（例如未來的自訂欄位）退回可編輯的
 * 文字欄。
 */
/** 只有手動輸入的 Task 欄位才能進表格（公式／彙總欄位不可由使用者直接編輯）。 */
function isManualTaskCustomField(field: CustomFieldDefinition): boolean {
  return field.scope === 'Task'
    && field.formula === null
    && field.rollup === 'None'
    && ['Text', 'Number', 'Cost', 'Date', 'Flag'].includes(field.kind);
}

const DEFAULT_CUSTOM_COLUMN_WIDTH = 120;

/** 自訂欄位 definition → grid 欄位定義。 */
function toCustomGridColumn(
  id: string,
  definition: CustomFieldDefinition,
  width: number | undefined,
): GridColumnDef {
  const kind: GridColumnDef['kind'] = definition.kind === 'Date'
    ? 'date'
    : definition.kind === 'Number'
      ? 'number'
      : definition.kind === 'Cost'
        ? 'currency'
        : 'text';
  return {
    id,
    field: definition.id as string,
    title: definition.name,
    kind,
    width: width ?? DEFAULT_CUSTOM_COLUMN_WIDTH,
    editable: true,
    custom: true,
    customFieldId: definition.id,
    customFieldKind: definition.kind as GridColumnDef['customFieldKind'],
    customFieldLookup: definition.lookup,
    customFieldDefaultValue: definition.defaultValue,
  };
}

export function toGridColumns(
  stored: readonly StoreColumnDef[],
  customFields: readonly CustomFieldDefinition[] = [],
): readonly GridColumnDef[] {
  const defaultsById = new Map(GRID_DEFAULT_COLUMNS.map((column) => [column.id, column]));
  // id 對不上時再用 field 對一次：store 與 grid 的 id 曾經分歧（'duration' vs
  // 'durationMinutes'），而舊的 .bproj 檔裡存的是舊 id，光統一原始碼救不回已存檔的專案。
  const defaultsByField = new Map(GRID_DEFAULT_COLUMNS.map((column) => [column.field, column]));
  const supportedCustomFields = new Map(
    customFields.filter(isManualTaskCustomField).map((field) => [field.id as string, field]),
  );
  const converted = stored
    .filter((column) => column.visible)
    .flatMap((column): readonly GridColumnDef[] => {
      const base = defaultsById.get(column.id) ?? defaultsByField.get(column.field);
      if (base !== undefined) return [{ ...base, width: column.width, title: column.label }];
      const definition = supportedCustomFields.get(column.field);
      if (definition === undefined) return [];
      return [toCustomGridColumn(column.id, definition, column.width)];
    });
  return converted.length > 0 ? converted : GRID_DEFAULT_COLUMNS;
}

/**
 * 「欄位設定」面板可供新增的完整欄位目錄:內建欄位 + 可手動編輯的 Task 自訂欄位。
 *
 * 與 `toGridColumns()` 的差別是它不看目前顯示了哪些欄位 —— 面板自己會扣掉已顯示的。
 */
export function buildAvailableGridColumns(
  customFields: readonly CustomFieldDefinition[] = [],
): readonly GridColumnDef[] {
  const custom = customFields
    .filter(isManualTaskCustomField)
    .map((definition) => toCustomGridColumn(definition.id as string, definition, undefined));
  return [...GRID_DEFAULT_COLUMNS, ...custom];
}

/** grid 的欄位定義寫回 store 的持久化形狀。 */
export function toStoreColumns(columns: readonly GridColumnDef[]): readonly StoreColumnDef[] {
  return columns.map((column) => ({
    id: column.id,
    field: column.field,
    label: column.title,
    width: column.width,
    visible: true,
  }));
}

// ---------------------------------------------------------------------------
// 任務表格 / 甘特圖
// ---------------------------------------------------------------------------

/**
 * 兩側共用的可見列。
 *
 * 甘特圖與表格**必須用同一份清單**，否則「第 n 列」在兩邊指的不是同一個任務，
 * 共用 `scrollTop` 就會對不齊。這也是為什麼摺疊過濾放在整合層做一次，而不是
 * 讓兩個元件各自處理。
 */
export function selectVisibleTasks(
  project: Project,
  collapsedTaskIds: readonly string[],
): readonly Task[] {
  return getVisibleTasks(project.tasks, collapsedTaskIds as readonly TaskId[]);
}

export function buildGanttTasks(
  project: Project,
  collapsedTaskIds: readonly string[],
): readonly GanttTask[] {
  const resourceNames = selectResourceNamesByTask(project);
  const baselines = selectBaselineTaskSnapshots(project);
  const expected = selectExpectedProgressByTask(project, progressAsOf(project));
  return selectVisibleTasks(project, collapsedTaskIds).map((task) => {
    const progress = expected.get(task.id as string);
    return toGanttTask(task, {
      resourceNames: resourceNames.get(task.id as string) ?? '',
      baseline: baselines.get(task.id as string) ?? null,
      behindPercent: progress?.status === 'behind' ? -progress.variancePercent : null,
    });
  });
}

// ---------------------------------------------------------------------------
// 資源工作表
// ---------------------------------------------------------------------------

/** 每分鐘費率 → 每小時費率（工作表顯示的是時薪）。 */
const hourlyRate = (perMinute: Resource['standardRate']): number => rawNumber(perMinute) * 60;

export function buildResourceSheetRows(project: Project): readonly ResourceSheetRow[] {
  const overallocated = selectOverallocatedResourceIds(project);
  const calendarNameById = new Map(project.calendars.map((cal) => [cal.id as string, cal.name]));
  const defaultCalendarName = calendarNameById.get(project.defaultCalendarId as string) ?? 'Standard';
  return project.resources.map((resource): ResourceSheetRow => {
    const hasTaskAssignments = project.assignments.some((assignment) => assignment.resourceId === resource.id);
    const calendarName =
      resource.calendarId === null
        ? defaultCalendarName
        : calendarNameById.get(resource.calendarId as string) ?? defaultCalendarName;
    return {
      id: resource.id,
      name: resource.name,
      type: resource.type,
      isBudget: resource.isBudget === true,
      canSetBudget: resource.isBudget === true || !hasTaskAssignments,
      initials: resource.initials,
      group: resource.group,
      maxUnits: resource.maxUnits,
      standardRate: hourlyRate(resource.standardRate),
      overtimeRate: hourlyRate(resource.overtimeRate),
      costPerUse: resource.costPerUse,
      accrueAt: resource.accrueAt,
      calendarId: resource.calendarId,
      calendarName,
      baseCalendarName: defaultCalendarName,
      isOverallocated: overallocated.has(resource.id as string),
    };
  });
}

// ---------------------------------------------------------------------------
// 使用狀況（資源 / 任務）
// ---------------------------------------------------------------------------

function assignmentsBy<K extends 'taskId' | 'resourceId'>(
  project: Project,
  key: K,
): ReadonlyMap<string, readonly Assignment[]> {
  const byKey = new Map<string, Assignment[]>();
  for (const assignment of project.assignments) {
    const id = assignment[key] as string;
    const list = byKey.get(id) ?? [];
    list.push(assignment);
    byKey.set(id, list);
  }
  return byKey;
}

export function buildResourceUsageGroups(project: Project): readonly UsageGroupRow[] {
  const daily = selectDailyByAssignment(project);
  const overallocatedDays = selectOverallocatedDaysByResource(project);
  const byResource = assignmentsBy(project, 'resourceId');
  const taskNameById = new Map(project.tasks.map((task) => [task.id as string, task.name]));

  return project.resources.map((resource): UsageGroupRow => {
    const days = overallocatedDays.get(resource.id as string) ?? new Set<string>();
    return {
      id: resource.id as string,
      label: resource.name,
      children: (byResource.get(resource.id as string) ?? []).map((assignment) => ({
        id: assignment.id,
        label: taskNameById.get(assignment.taskId as string) ?? String(assignment.taskId),
        daily: (daily.get(assignment.id as string) ?? []).map((value) => ({
          ...value,
          overallocated: days.has(value.date),
        })),
      })),
    };
  });
}

export function buildTaskUsageGroups(project: Project): readonly UsageGroupRow[] {
  const daily = selectDailyByAssignment(project);
  const overallocatedDays = selectOverallocatedDaysByResource(project);
  const byTask = assignmentsBy(project, 'taskId');
  const resourceNameById = new Map(project.resources.map((r) => [r.id as string, r.name]));

  return project.tasks
    .filter((task) => !task.isSummary)
    .map((task): UsageGroupRow => ({
      id: task.id as string,
      label: task.name,
      children: (byTask.get(task.id as string) ?? []).map((assignment) => {
        const days = overallocatedDays.get(assignment.resourceId as string) ?? new Set<string>();
        return {
          id: assignment.id,
          label: resourceNameById.get(assignment.resourceId as string) ?? String(assignment.resourceId),
          daily: (daily.get(assignment.id as string) ?? []).map((value) => ({
            ...value,
            overallocated: days.has(value.date),
          })),
        };
      }),
    }));
}

// ---------------------------------------------------------------------------
// 資源長條圖
// ---------------------------------------------------------------------------

/** 一天的標準工時（分鐘），用於把「逐日工時」換算回「投入單位」。 */
const MINUTES_PER_STANDARD_DAY = 480;

export function buildResourceGraphSeries(project: Project): readonly ResourceGraphSeries[] {
  const daily = selectDailyByAssignment(project);
  const byResource = assignmentsBy(project, 'resourceId');

  return project.resources
    .filter((resource) => (byResource.get(resource.id as string) ?? []).length > 0)
    .map((resource): ResourceGraphSeries => {
      const unitsByDate = new Map<string, number>();
      for (const assignment of byResource.get(resource.id as string) ?? []) {
        for (const value of daily.get(assignment.id as string) ?? []) {
          const units = (value.hours * 60) / MINUTES_PER_STANDARD_DAY;
          unitsByDate.set(value.date, (unitsByDate.get(value.date) ?? 0) + units);
        }
      }
      const dates = [...unitsByDate.keys()].sort();
      return {
        resourceId: resource.id,
        resourceName: resource.name,
        maxUnits: rawNumber(resource.maxUnits),
        points: dates.map((date) => ({
          periodStart: date,
          label: date.slice(5),
          allocatedUnits: unitsByDate.get(date) ?? 0,
        })),
      };
    });
}

// ---------------------------------------------------------------------------
// 成本表
// ---------------------------------------------------------------------------

export function buildCostTableRows(project: Project): readonly CostTableRow[] {
  const cost = selectCostResult(project);
  const taskById = new Map(project.tasks.map((task) => [task.id as string, task]));
  if (cost === null) {
    // 引擎不可用時仍顯示任務結構與固定成本，比整片空白有用。
    return project.tasks.map((task) => ({
      taskId: task.id,
      wbs: task.wbs,
      name: task.name,
      outlineLevel: task.outlineLevel,
      parentId: task.parentId,
      isSummary: task.isSummary,
      fixedCost: rawNumber(task.fixedCost),
      totalCost: rawNumber(task.fixedCost),
      baselineCost: null,
      variance: null,
      actualCost: 0,
      remainingCost: rawNumber(task.fixedCost),
    }));
  }
  return cost.byTask.map((entry): CostTableRow => {
    const task = taskById.get(entry.taskId as string);
    return {
      taskId: entry.taskId,
      wbs: task?.wbs ?? '',
      name: task?.name ?? String(entry.taskId),
      outlineLevel: task?.outlineLevel ?? 1,
      parentId: task?.parentId ?? null,
      isSummary: task?.isSummary ?? false,
      fixedCost: rawNumber(entry.fixedCost),
      totalCost: rawNumber(entry.cost),
      baselineCost: entry.baselineCost === null ? null : rawNumber(entry.baselineCost),
      variance: entry.variance === null ? null : rawNumber(entry.variance),
      actualCost: rawNumber(entry.actualCost),
      remainingCost: rawNumber(entry.remainingCost),
    };
  });
}

// ---------------------------------------------------------------------------
// EVM 報表
// ---------------------------------------------------------------------------

export interface EvmReportData {
  readonly metrics: EvmMetricsView;
  readonly curve: readonly EvmCurvePoint[];
  readonly eacComparison: readonly EacComparisonEntry[];
  readonly diagnostics: readonly EvmDiagnosticView[];
  readonly statusDate: string;
}

const EMPTY_METRICS: EvmMetricsView = {
  pv: 0,
  ev: 0,
  ac: 0,
  sv: 0,
  cv: 0,
  spi: null,
  cpi: null,
  bac: 0,
  eac: 0,
  etc: 0,
  vac: 0,
  tcpi: null,
};

/**
 * EVM 報表資料。
 *
 * 引擎完全不可用（拋錯）時，仍合成一筆 `EVM_NO_BASELINE` 診斷 —— `EvmReport`
 * 以這個 code 決定要顯示「尚未設定基準線」的引導語而不是滿版 N/A，這正是
 * 「引擎沒資料」時使用者最需要看到的下一步。
 */
export function buildEvmReportData(project: Project): EvmReportData {
  const result = selectEvmResult(project);
  const statusDate = (project.statusDate ?? project.finishDate) as string;
  if (result === null) {
    return {
      metrics: EMPTY_METRICS,
      curve: [],
      eacComparison: [],
      diagnostics: [{ severity: 'Warning', code: 'EVM_NO_BASELINE' }],
      statusDate: statusDate.slice(0, 10),
    };
  }
  return {
    metrics: {
      pv: rawNumber(result.project.pv),
      ev: rawNumber(result.project.ev),
      ac: rawNumber(result.project.ac),
      sv: rawNumber(result.project.sv),
      cv: rawNumber(result.project.cv),
      spi: result.project.spi,
      cpi: result.project.cpi,
      bac: rawNumber(result.project.bac),
      eac: rawNumber(result.project.eac),
      etc: rawNumber(result.project.etc),
      vac: rawNumber(result.project.vac),
      tcpi: result.project.tcpi,
    },
    curve: result.curve.map((point) => ({
      date: (point.date as string).slice(0, 10),
      pv: rawNumber(point.pv),
      ev: rawNumber(point.ev),
      ac: rawNumber(point.ac),
    })),
    eacComparison: selectEacComparison(project).map((entry) => ({
      method: entry.method,
      eac: rawNumber(entry.metrics.eac),
    })),
    diagnostics: (result.diagnostics ?? []).map((diagnostic) => ({
      severity: diagnostic.severity,
      code: diagnostic.code,
    })),
    statusDate: (result.statusDate as string).slice(0, 10),
  };
}

// ---------------------------------------------------------------------------
// 撫平前後對照
// ---------------------------------------------------------------------------

export interface LevelingComparisonData {
  readonly rows: readonly LevelingComparisonRow[];
  readonly originalFinish: string;
  readonly leveledFinish: string;
  readonly finishDelayMinutes: number;
  readonly remainingOverallocationCount: number;
}

export function buildLevelingComparison(
  before: Project,
  result: LevelingResult,
): LevelingComparisonData {
  const taskById = new Map(before.tasks.map((task) => [task.id as string, task]));
  return {
    rows: result.adjustments.map((adjustment): LevelingComparisonRow => {
      const task = taskById.get(adjustment.taskId as string);
      return {
        taskId: adjustment.taskId,
        taskName: task?.name ?? String(adjustment.taskId),
        originalStart: (task?.start ?? adjustment.newStart) as string,
        leveledStart: adjustment.newStart as string,
        delayMinutes: rawNumber(adjustment.levelingDelay),
      };
    }),
    originalFinish: before.finishDate as string,
    leveledFinish: result.projectFinish as string,
    finishDelayMinutes: rawNumber(result.finishDelay),
    remainingOverallocationCount: result.remainingOverallocations.length,
  };
}

export type { BaselineNumber };
